import { logError, logInformation } from '@core/logging';
import { ApiResponse, BaseApiResponse, HttpResponse } from '@core/swagger-client';
import { useContext, useRef, useState } from 'react';
import { serializeError } from 'serialize-error';
import { AuthContext } from '../components';

export const useChunkUpload = (url: string) => {
    const chunkSize = 10 * 1024 * 1024; // 10MB
    const [progress, setProgress] = useState(0);
    const [isDone, setIsDone] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [canResume, setCanResume] = useState(false);

    const cancelRef = useRef(false);
    const pauseRef = useRef(false);

    const fileRef = useRef<File | null>(null);
    const totalChunksRef = useRef(0);
    const currentChunkIndexRef = useRef(0);

    const authorizationToken = useContext(AuthContext).token;

    const pause = () => {
        pauseRef.current = true;
    };

    const cancel = () => {
        if (!fileRef.current) {
            return;
        }

        cancelRef.current = true;
        if (isUploading) {
            return;
        }

        const headers = new Headers();
        headers.append('Authorization', `Bearer ${authorizationToken}`);

        const formData = new FormData();
        formData.append('fileName', fileRef.current.name);

        // If not uploading, we still need to cleanup the old upload
        fetch(url, {
            method: 'DELETE',
            body: formData,
            headers: headers
        })
            .then(() => {
                logInformation('File upload cancelled', { FileName: fileRef.current?.name, Url: url });
            })
            .catch((error) => {
                logError('Error cancelling file upload', { Error: serializeError(error), FileName: fileRef.current?.name, Url: url });
            });

        setIsDone(true);
        setCanResume(false);
        setIsUploading(false);
        setProgress(0);

        fileRef.current = null;
        totalChunksRef.current = 0;
        currentChunkIndexRef.current = 0;
    };

    const internalUpload = async (): Promise<BaseApiResponse> => {
        cancelRef.current = false;
        pauseRef.current = false;

        setIsUploading(true);
        setCanResume(true);

        let result: BaseApiResponse | null = null;
        for (let i = currentChunkIndexRef.current; i < totalChunksRef.current; i++) {
            if (!fileRef.current) {
                setIsUploading(false);
                return {
                    success: false,
                    message: 'No file selected for upload',
                    statusCode: 400,
                    errorCode: 'NoFileSelected'
                };
            }

            currentChunkIndexRef.current = i;

            logInformation('Uploading file chunk', { ChunkIndex: i, FileName: fileRef.current.name, FileSize: fileRef.current.size, Url: url });

            const start = i * chunkSize;
            const end = Math.min(start + chunkSize, fileRef.current.size);
            const chunk = fileRef.current.slice(start, end);

            const formData = new FormData();
            formData.append('file', chunk, fileRef.current.name);
            formData.append('chunkSize', chunkSize.toString());
            formData.append('chunkIndex', i.toString());
            formData.append('totalChunks', totalChunksRef.current.toString());
            formData.append('fileName', fileRef.current.name);
            formData.append('fileSize', fileRef.current.size.toString());

            try {
                const headers = new Headers();
                headers.append('Authorization', `Bearer ${authorizationToken}`);

                var cancelUpload = cancelRef.current;
                const response = (await fetch(url, {
                    method: cancelUpload ? 'DELETE' : 'POST',
                    body: formData,
                    headers: headers
                })) as HttpResponse<ApiResponse<boolean>, BaseApiResponse>;

                try {
                    result = await response.json();
                    result ||= {
                        success: response.ok,
                        message: response.statusText,
                        statusCode: response.status,
                        errorCode: 'UnknownError'
                    };
                } catch (error) {
                    logError('Error parsing upload response', { Error: serializeError(error), ChunkIndex: i, FileName: fileRef.current.name, Url: url });
                    result = {
                        success: false,
                        message: error ? JSON.stringify(serializeError(error)) : 'Error parsing response',
                        statusCode: response.status,
                        errorCode: 'ResponseParseError'
                    };
                }

                if (result.success !== true || cancelUpload || pauseRef.current) {
                    setIsUploading(false);

                    if (!response.ok) {
                        logError('Error uploading file chunk', {
                            Error: serializeError(response.error),
                            ChunkIndex: i,
                            FileName: fileRef.current.name,
                            FileSize: fileRef.current.size,
                            Url: url
                        });
                    } else {
                        if (pauseRef.current) {
                            logInformation('File upload paused', { FileName: fileRef.current.name, FileSize: fileRef.current.size, ChunkIndex: i, Url: url });

                            // This upload is paused, but the current chunk was uploaded successfully
                            currentChunkIndexRef.current = i + 1;
                        } else if (cancelUpload) {
                            logInformation('File upload cancelled', {
                                FileName: fileRef.current.name,
                                FileSize: fileRef.current.size,
                                ChunkIndex: i,
                                Url: url
                            });

                            // Reset upload state on cancel
                            fileRef.current = null;
                            totalChunksRef.current = 0;
                            currentChunkIndexRef.current = 0;

                            // We can't resume a canceled upload
                            setCanResume(false);
                            setIsDone(true);
                        }
                    }
                    return result;
                }
            } catch (error) {
                logError('Error uploading file chunk', { Error: serializeError(error), ChunkIndex: i, FileName: fileRef.current?.name, Url: url });
                setIsUploading(false);
                return {
                    success: false,
                    message: error ? JSON.stringify(serializeError(error)) : 'Error parsing response',
                    statusCode: 500,
                    errorCode: 'UploadError'
                };
            }

            setProgress(Math.round(((i + 1) / totalChunksRef.current) * 100));
        }

        setProgress(100);
        setIsUploading(false);
        setCanResume(false);
        setIsDone(true);

        if (fileRef.current) {
            logInformation('File upload completed', { FileName: fileRef.current.name, FileSize: fileRef.current.size, Url: url });
        }

        return (
            result || {
                success: true,
                message: 'File uploaded successfully',
                statusCode: 200
            }
        );
    };

    const resume = (): Promise<BaseApiResponse> => {
        if (!fileRef.current || totalChunksRef.current === 0 || currentChunkIndexRef.current >= totalChunksRef.current) {
            throw new Error('No upload to resume');
        }

        logInformation('Resuming file upload', { FileName: fileRef.current.name, FileSize: fileRef.current.size, Url: url });
        return internalUpload();
    };

    const upload = async (file: File): Promise<BaseApiResponse> => {
        logInformation('Starting file upload', { FileName: file.name, FileSize: file.size, Url: url });

        fileRef.current = file;
        totalChunksRef.current = Math.ceil(file.size / chunkSize);
        currentChunkIndexRef.current = 0;

        return internalUpload();
    };

    return { upload, resume, pause, cancel, isUploading, isDone, progress, canResume };
};
